/**
 * Premium Animations for Servitix Homepage
 * GPU-accelerated animations with performance optimizations
 */

// Check if user prefers reduced motion
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Intersection Observer for scroll-triggered animations
 */
function initScrollAnimations() {
    if (prefersReducedMotion) return;

    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');

                // For staggered animations
                if (entry.target.hasAttribute('data-stagger')) {
                    const children = entry.target.querySelectorAll('[data-stagger-item]');
                    children.forEach((child, index) => {
                        setTimeout(() => {
                            child.classList.add('is-visible');
                        }, index * 100);
                    });
                }
            }
        });
    }, observerOptions);

    // Observe elements with animation classes
    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    document.querySelectorAll('[data-stagger]').forEach(el => observer.observe(el));
}

/**
 * Parallax scrolling effect for hero section
 */
function initParallax() {
    if (prefersReducedMotion) return;

    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (parallaxElements.length === 0) return;

    let ticking = false;

    function updateParallax() {
        const scrollTop = window.pageYOffset;

        parallaxElements.forEach(element => {
            const speed = parseFloat(element.getAttribute('data-parallax')) || 0.5;
            const yPos = -(scrollTop * speed);

            // Use transform for GPU acceleration
            element.style.transform = `translate3d(0, ${yPos}px, 0)`;
        });

        ticking = false;
    }

    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(updateParallax);
            ticking = true;
        }
    }, { passive: true });
}

/**
 * Animated counter for numbers
 */
function animateCounter(element, duration = 2000) {
    if (prefersReducedMotion) {
        element.textContent = element.getAttribute('data-target');
        return;
    }

    const target = parseInt(element.getAttribute('data-target'));
    const start = 0;
    const startTime = performance.now();

    function update(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (easeOutQuad)
        const easeProgress = progress * (2 - progress);
        const current = Math.floor(start + (target - start) * easeProgress);

        element.textContent = current;

        if (progress < 1) {
            requestAnimationFrame(update);
        } else {
            element.textContent = target;
        }
    }

    requestAnimationFrame(update);
}

/**
 * Animated star ratings
 */
function initStarAnimations() {
    if (prefersReducedMotion) return;

    const observerOptions = {
        threshold: 0.5,
        rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const stars = entry.target.querySelectorAll('.star-animate');
                stars.forEach((star, index) => {
                    setTimeout(() => {
                        star.classList.add('star-filled');
                    }, index * 100);
                });
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);

    document.querySelectorAll('[data-star-container]').forEach(el => observer.observe(el));
}

/**
 * Animated checkmarks for pricing table
 */
function initCheckmarkAnimations() {
    if (prefersReducedMotion) return;

    const observerOptions = {
        threshold: 0.3,
        rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const checks = entry.target.querySelectorAll('.check-animate');
                checks.forEach((check, index) => {
                    setTimeout(() => {
                        check.classList.add('check-drawn');
                    }, index * 50);
                });
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);

    document.querySelectorAll('[data-check-container]').forEach(el => observer.observe(el));
}

/**
 * Shimmer effect for special elements
 */
function addShimmerEffect(element) {
    if (prefersReducedMotion) return;

    element.classList.add('shimmer-effect');
}

/**
 * Initialize all premium animations
 */
function initPremiumAnimations() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init() {
        initScrollAnimations();
        initParallax();
        initStarAnimations();
        initCheckmarkAnimations();

        // Initialize counters
        document.querySelectorAll('[data-counter]').forEach(counter => {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        animateCounter(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.5 });

            observer.observe(counter);
        });

        // Add shimmer to special badges
        document.querySelectorAll('[data-shimmer]').forEach(addShimmerEffect);
    }
}

// Initialize animations
initPremiumAnimations();
